// =============================================================================
// components/learn/LevelPills.tsx — Beginner / Intermediate / Expert tab pills.
//
// Reference design: pill-style level tabs where the selected pill is a light
// purple fill. Unselected pills are quiet glass chips. Counts (optional) give
// the "course progress" feel without inventing content.
// =============================================================================
import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { colors, spacing, radius, fonts } from '../../constants/theme';
import { LEARN_LEVELS, LearnLevel } from './gradients';

interface Props {
  selected: LearnLevel;
  onSelect: (level: LearnLevel) => void;
  counts?: Partial<Record<LearnLevel, number>>;
}

export default function LevelPills({ selected, onSelect, counts }: Props) {
  return (
    <View style={styles.row}>
      {LEARN_LEVELS.map((level) => {
        const active = selected === level.value;
        const count = counts?.[level.value];
        return (
          <TouchableOpacity
            key={level.value}
            style={[styles.pill, active && styles.pillActive]}
            onPress={() => onSelect(level.value)}
            activeOpacity={0.75}
            accessibilityRole="tab"
            accessibilityLabel={`${level.label} level`}
            accessibilityState={{ selected: active }}
          >
            <Text style={[styles.pillText, active && styles.pillTextActive]}>
              {level.label}
            </Text>
            {typeof count === 'number' && count > 0 && (
              <View style={[styles.countBadge, active && styles.countBadgeActive]}>
                <Text style={[styles.countText, active && styles.countTextActive]}>{count}</Text>
              </View>
            )}
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs + 2,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm + 2,
    borderRadius: radius.full,
    backgroundColor: 'rgba(255,255,255,0.6)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.85)',
  },
  pillActive: {
    // The reference "light purple pill": soft primary fill + primary text.
    backgroundColor: colors.primary + '22',
    borderColor: colors.primary + '55',
  },
  pillText: {
    fontSize: 14,
    color: colors.muted,
    ...fonts.semibold,
  },
  pillTextActive: {
    color: colors.primary,
    ...fonts.bold,
  },
  countBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    paddingHorizontal: 5,
    backgroundColor: colors.faint + '55',
    alignItems: 'center',
    justifyContent: 'center',
  },
  countBadgeActive: {
    backgroundColor: colors.primary,
  },
  countText: {
    fontSize: 11,
    color: colors.ink2,
    ...fonts.bold,
  },
  countTextActive: {
    color: colors.white,
  },
});
